/**
 * Normalizes a path by removing query parameters, trailing slashes, and handling edge cases
 * @param path - The path to normalize
 * @returns The normalized path
 * @throws {Error} If path is null or undefined
 */
export function normalizePath(path: string): string {
  if (path == null) {
    throw new Error('Path cannot be null or undefined');
  }

  // Remove query parameters and hash
  const pathWithoutQuery = path.split('?')[0].split('#')[0];

  // Handle relative paths
  const normalizedPath = pathWithoutQuery
    .replace(/^\.\/|^\.\.\//, '') // Remove ./ or ../
    .replace(/\/+/g, '/') // Replace multiple slashes with single slash
    .replace(/\/$/, ''); // Remove trailing slash

  return normalizedPath;
}

/**
 * Determines if a current path matches a link path based on exact or partial matching rules
 * @param currentPath - The current path to check
 * @param linkPath - The link path to match against
 * @param isExact - Whether to use exact matching rules
 * @param associatedPaths - Additional paths that should be considered matches
 * @returns boolean indicating if paths match
 * @throws {Error} If currentPath or linkPath is null or undefined
 */
export function isPathMatch(
  currentPath: string,
  linkPath: string,
  isExact = false,
  associatedPaths: string[] = []
): boolean {
  if (currentPath == null || linkPath == null) {
    throw new Error('Paths cannot be null or undefined');
  }

  const normalizedCurrentPath = normalizePath(currentPath);
  const normalizedLinkPath = normalizePath(linkPath);

  // Normalize associated paths once
  const normalizedAssociatedPaths = associatedPaths.map(normalizePath);

  if (isExact) {
    return (
      normalizedCurrentPath === normalizedLinkPath ||
      normalizedAssociatedPaths.includes(normalizedCurrentPath)
    );
  }

  return (
    normalizedCurrentPath === normalizedLinkPath ||
    normalizedCurrentPath.startsWith(`${normalizedLinkPath}/`) ||
    normalizedAssociatedPaths.some((path) => normalizedCurrentPath.startsWith(`${path}/`))
  );
}
